"use client";

import type { CountryCode } from "libphonenumber-js";
import React, { useEffect, useRef, useState } from "react";
import {
  AsYouType,
  getExampleNumber,
  isValidPhoneNumber,
  parsePhoneNumber,
} from "libphonenumber-js";
import examples from "libphonenumber-js/examples.mobile.json";

import { Input } from "@fluid-app/ui";

import type { CountryOption } from "../country-selector";
import { CountrySelector } from "../country-selector";

export interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  id?: string;
  name?: string;
  placeholder?: string;
  required?: boolean;
  className?: string;
  countryValue?: string;
  onCountryChange?: (value: string) => void;
}

export function PhoneInput({
  value,
  onChange,
  error,
  id = "phone",
  name = "phone",
  placeholder,
  required = false,
  className,
  countryValue = "US",
  onCountryChange,
}: PhoneInputProps) {
  // State to track the selected country
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(
    countryValue as CountryCode,
  );
  const [formattedValue, setFormattedValue] = useState<string>(value);
  const [rawValue, setRawValue] = useState<string>(value.replace(/\D/g, "")); // Store digits only
  const inputRef = useRef<HTMLInputElement>(null);

  // Generate placeholder based on selected country if none provided
  const getPlaceholder = () => {
    if (placeholder) return placeholder;

    try {
      const exampleNumber = getExampleNumber(selectedCountry, examples);
      return exampleNumber ? exampleNumber.formatNational() : "Phone number";
    } catch {
      return "Phone number";
    }
  };

  // Format the phone number based on the input and selected country
  const formatPhoneNumber = (input: string, country: CountryCode) => {
    if (!input) return "";
    return new AsYouType(country).input(input);
  };

  // Convert phone number to E.164 format
  const toE164Format = (input: string, country: CountryCode): string => {
    if (!input) return "";

    try {
      // Try to parse the phone number
      if (isValidPhoneNumber(input, country)) {
        const phoneNumber = parsePhoneNumber(input, country);
        return phoneNumber.format("E.164"); // Returns +CountryCodeAreaCodeLocalNumber
      }

      // If not yet valid, just return the input
      return input;
    } catch {
      // If there's any error in parsing, return the original input
      return input;
    }
  };

  // Update the formatted value when the rawValue or country changes
  useEffect(() => {
    const formatted = formatPhoneNumber(rawValue, selectedCountry);
    setFormattedValue(formatted);

    // Update parent with E.164 format if valid
    const e164Value = toE164Format(formatted, selectedCountry);
    if (e164Value !== value) {
      onChange(e164Value);
    }
  }, [rawValue, selectedCountry, onChange, value]);

  // Handle country change
  const handleCountryChange = (countryCode: string) => {
    setSelectedCountry(countryCode as CountryCode);

    if (onCountryChange) {
      onCountryChange(countryCode);
    }
  };

  // Handle input change by extracting just the digits
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;

    // Special handling for backspace/delete - if we're removing characters
    if (input.length < formattedValue.length) {
      // Extract only the digits from the current input
      const digitsOnly = input.replace(/\D/g, "");
      // If we're removing a digit, update the raw value
      if (digitsOnly.length < rawValue.length) {
        setRawValue(digitsOnly);
      }
      // Otherwise we're probably just removing a formatting character, so keep the raw value
      // but update the formatted value to help with cursor position
      else {
        const formatted = formatPhoneNumber(rawValue, selectedCountry);
        setFormattedValue(formatted);
      }
    } else {
      // If adding characters, just extract digits and update
      const digitsOnly = input.replace(/\D/g, "");
      setRawValue(digitsOnly);
    }
  };

  // Handle keydown events for better backspace behavior
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Backspace handling
    if (e.key === "Backspace" && inputRef.current) {
      const input = inputRef.current;
      const cursorPos = input.selectionStart;

      // If cursor is after a non-digit character, move cursor back before deleting
      if (cursorPos && cursorPos > 0) {
        const charBeforeCursor = formattedValue.charAt(cursorPos - 1);
        if (/\D/.test(charBeforeCursor)) {
          e.preventDefault();
          // Find the previous digit position
          let newPos = cursorPos - 1;
          while (newPos > 0 && /\D/.test(formattedValue.charAt(newPos - 1))) {
            newPos--;
          }

          // Set selection to that position
          input.setSelectionRange(newPos, newPos);

          // Update the raw value by removing the digit at the new position
          if (newPos > 0) {
            const newRawValue =
              rawValue.slice(0, newPos - 1) + rawValue.slice(newPos);
            setRawValue(newRawValue);
          }
        }
      }
    }
  };

  // Handle country selection via CountrySelector
  const handleCountrySelect = (country: CountryOption) => {
    handleCountryChange(country.code);
  };

  return (
    <div className="flex w-full">
      <div className="mr-2 w-32">
        <CountrySelector
          value={selectedCountry}
          onChange={handleCountryChange}
          onSelect={handleCountrySelect}
        />
      </div>
      <Input
        ref={inputRef}
        id={id}
        name={name}
        value={formattedValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={getPlaceholder()}
        required={required}
        className={className}
        aria-invalid={!!error}
        type="tel"
      />
    </div>
  );
}
